import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Loader2, Shield, ShieldCheck, ShieldAlert, Copy, Download, Eye, EyeOff } from 'lucide-react';
import { mfaAPI } from '../lib/api';
import { toast } from 'sonner';

const MFASecurity = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Setup dialog state
  const [showSetup, setShowSetup] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [setupCode, setSetupCode] = useState('');
  const [setupLoading, setSetupLoading] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  
  // Backup codes dialog state
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [backupCodes, setBackupCodes] = useState([]);
  
  // Disable dialog state
  const [showDisable, setShowDisable] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [disableLoading, setDisableLoading] = useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const response = await mfaAPI.getStatus();
      setStatus(response.data);
    } catch (error) {
      toast.error('Failed to load MFA status');
    } finally {
      setLoading(false);
    }
  };

  const handleStartSetup = async () => {
    setSetupLoading(true);
    try {
      const response = await mfaAPI.setup();
      setSetupData(response.data);
      setShowSetup(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start MFA setup');
    } finally {
      setSetupLoading(false);
    }
  };

  const handleConfirmSetup = async () => {
    if (setupCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    setSetupLoading(true);
    try {
      const response = await mfaAPI.confirm(setupCode);
      setBackupCodes(response.data.backup_codes);
      setShowSetup(false);
      setShowBackupCodes(true);
      setSetupCode('');
      loadStatus();
      toast.success('MFA enabled successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code. Please try again.');
    } finally {
      setSetupLoading(false);
    }
  };

  const handleDisable = async () => {
    if (!disablePassword || !disableCode) {
      toast.error('Please enter your password and MFA code');
      return;
    }
    
    setDisableLoading(true);
    try {
      await mfaAPI.disable(disablePassword, disableCode);
      setShowDisable(false);
      setDisablePassword('');
      setDisableCode('');
      loadStatus();
      toast.success('MFA disabled successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disable MFA');
    } finally {
      setDisableLoading(false);
    }
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    toast.success('Backup codes copied to clipboard');
  };

  const downloadBackupCodes = () => {
    const content = `Stock & Load - MFA Backup Codes\n\nGenerated: ${new Date().toLocaleString()}\n\nIMPORTANT: Store these codes in a safe place. Each code can only be used once.\n\n${backupCodes.join('\n')}`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mfa-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            <CardTitle>Two-Factor Authentication</CardTitle>
          </div>
          <CardDescription>
            Add an extra layer of security to your account using an authenticator app
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {status?.enabled ? (
                <>
                  <ShieldCheck className="h-8 w-8 text-green-500" />
                  <div>
                    <p className="font-medium">MFA is enabled</p>
                    <p className="text-sm text-muted-foreground">
                      Your account is protected with two-factor authentication
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <ShieldAlert className="h-8 w-8 text-yellow-500" />
                  <div>
                    <p className="font-medium">MFA is not enabled</p>
                    <p className="text-sm text-muted-foreground">
                      Enable MFA to add an extra layer of security
                    </p>
                  </div>
                </>
              )}
            </div>
            
            {status?.required && (
              <Badge variant="destructive">Required</Badge>
            )}
          </div>

          {status?.required && !status?.enabled && (
            <Alert variant="destructive">
              <AlertDescription>
                Your organization requires MFA. Please enable it to continue using the application.
              </AlertDescription>
            </Alert>
          )}

          <div className="flex gap-2">
            {!status?.enabled ? (
              <Button onClick={handleStartSetup} disabled={setupLoading}>
                {setupLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Setting up...
                  </>
                ) : (
                  'Enable MFA'
                )}
              </Button>
            ) : (
              <Button
                variant="destructive"
                onClick={() => setShowDisable(true)}
                disabled={!status?.can_disable}
              >
                Disable MFA
              </Button>
            )}
          </div>

          {status?.enabled && !status?.can_disable && (
            <p className="text-sm text-muted-foreground">
              MFA cannot be disabled because it is required by your organization.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Setup Dialog */}
      <Dialog open={showSetup} onOpenChange={setShowSetup}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Set Up Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              Scan the QR code with your authenticator app (Google Authenticator, Authy, etc.)
            </DialogDescription>
          </DialogHeader>
          
          {setupData && (
            <div className="space-y-4">
              <div className="flex justify-center bg-white p-4 rounded-lg">
                {setupData.qr_code_svg?.startsWith('data:') ? (
                  <img 
                    src={setupData.qr_code_svg} 
                    alt="MFA QR Code" 
                    className="w-48 h-48"
                  />
                ) : (
                  <div 
                    dangerouslySetInnerHTML={{ __html: setupData.qr_code_svg }} 
                    className="w-48 h-48"
                  />
                )}
              </div>

              <div className="space-y-2">
                <Label>Or enter this code manually:</Label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-muted px-3 py-2 rounded text-sm font-mono">
                    {showSecret ? setupData.secret : '••••••••••••••••••••••••••••••••'}
                  </code>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowSecret(!showSecret)}
                  >
                    {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      navigator.clipboard.writeText(setupData.secret);
                      toast.success('Secret copied');
                    }}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Enter the 6-digit code from your app:</Label>
                <Input
                  type="text"
                  placeholder="000000"
                  maxLength={6}
                  value={setupCode}
                  onChange={(e) => setSetupCode(e.target.value.replace(/\D/g, ''))}
                  className="text-center text-2xl tracking-widest"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSetup(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmSetup} disabled={setupLoading || setupCode.length !== 6}>
              {setupLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify & Enable'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Backup Codes Dialog */}
      <Dialog open={showBackupCodes} onOpenChange={setShowBackupCodes}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Save Your Backup Codes</DialogTitle>
            <DialogDescription>
              Store these codes somewhere safe. Each code can only be used once.
            </DialogDescription>
          </DialogHeader>
          
          <Alert variant="destructive">
            <AlertDescription>
              <strong>Important:</strong> These codes will only be shown once. If you lose your authenticator app, you'll need these codes to access your account.
            </AlertDescription>
          </Alert>

          <div className="grid grid-cols-2 gap-2 my-4">
            {backupCodes.map((code, index) => (
              <code key={index} className="bg-muted px-3 py-2 rounded text-center font-mono text-sm">
                {code}
              </code>
            ))}
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={copyBackupCodes} className="flex-1">
              <Copy className="h-4 w-4 mr-2" />
              Copy
            </Button>
            <Button variant="outline" onClick={downloadBackupCodes} className="flex-1">
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </div>

          <DialogFooter>
            <Button onClick={() => setShowBackupCodes(false)}>
              I've saved my codes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Disable Dialog */}
      <Dialog open={showDisable} onOpenChange={setShowDisable}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
            <DialogDescription>
              Enter your password and a code from your authenticator app to disable MFA.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Password</Label>
              <Input
                type="password"
                placeholder="Your current password"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Authentication Code</Label>
              <Input
                type="text"
                placeholder="6-digit code or backup code"
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisable(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDisable}
              disabled={disableLoading || !disablePassword || !disableCode}
            >
              {disableLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Disabling...
                </>
              ) : (
                'Disable MFA'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MFASecurity;
